import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, EMPTY, filter, from, map, mergeMap, Observable, of } from 'rxjs';

import { AuthEvent, AuthTelemetryReporter, Command } from '@ninety/mobile/platform/auth';

import { SessionActions } from './session.actions';

type CommandOf<D extends Command['do']> = Extract<Command, { do: D }>;

/**
 * The seam between a Command the machine decided on and the code that performs it.
 *
 * `transition()` returns Commands as data and `SessionEffects` emits one `commandIssued` per
 * Command. This is the single executor `Command`'s own doc names — the only thing that turns those
 * back into work.
 *
 * ## A handler reports its own failures
 *
 * A handler returns the `AuthEvent` its outcome means, including for failure, because only it
 * knows what its failure means: a rejected `srpSignIn` is `grantFailed` and a rejected
 * `sessionRestore` is `refreshFailed`, and the machine cannot infer that from an exception.
 *
 * An error that escapes anyway is a bug rather than a domain outcome, so no cause is guessed at —
 * guessing is how someone gets signed out over a bug. What is dispatched instead is
 * `commandFailed`, which says only "the Command you are waiting on will not answer". It is enough
 * for the state to end its wait: leaving the state alone instead leaves a failed `srpSignIn` or a
 * failed `biometricSignIn` behind a spinner with nothing in flight to clear it.
 *
 * `auth.command_failed` is still reported alongside, and is still the alertable signal.
 *
 * ## Why `mergeMap` and not `concatMap`
 *
 * Ordering across Commands is not a correctness requirement — `SessionEffects` already
 * guarantees the state has advanced before any Command runs. Serialising them would let one slow
 * Command block every later one, and `sessionRestore` carries a deliberate backoff delay, so a
 * queued `navigate` behind a retrying restore would strand the UI.
 */
@Injectable({ providedIn: 'root' })
export class CommandRunner {
  private readonly actions$ = inject(Actions);
  private readonly reporter = inject(AuthTelemetryReporter);

  /**
   * Every issue of `verb`, for a handler whose answer is an ordinary app action rather than an
   * `AuthEvent`.
   *
   * The app-integration Commands — `navigate`, `notifyAppSignedIn` — tell the rest of the app
   * something instead of telling the machine something, so they have no outcome to report back and
   * {@link on}'s signature does not fit. Routing them through here keeps the rule intact, whichever
   * direction the answer goes.
   */
  issued<D extends Command['do']>(verb: D): Observable<CommandOf<D>> {
    return this.actions$.pipe(
      ofType(SessionActions.commandIssued),
      filter(({ command }) => command.do === verb),
      map(({ command }) => command as CommandOf<D>)
    );
  }

  /**
   * Run `handle` whenever the machine issues `verb`, and dispatch the event it reports.
   *
   * For the Commands the machine *waits on*. An error that escapes `handle` becomes
   * `commandFailed`, so the waiting state hears something either way — see {@link teardown} for the
   * Commands where that would be wrong.
   */
  on<D extends Command['do']>(verb: D, handle: (command: CommandOf<D>) => Promise<AuthEvent>): Observable<Action> {
    return this.issued(verb).pipe(
      mergeMap(command =>
        from(handle(command)).pipe(
          map(event => SessionActions.eventReceived({ event })),
          catchError(() => {
            this.reporter.commandFailed(command);
            return of(SessionActions.eventReceived({ event: { type: 'commandFailed', verb } }));
          })
        )
      )
    );
  }

  /**
   * Run `handle` whenever the machine issues `verb`, and report an event only if it failed.
   *
   * The third shape, and the three are distinguished by what a *success* means rather than by
   * convenience: {@link on} is for a Command whose outcome the machine is waiting to hear,
   * {@link teardown} for one it has stopped caring about, and this for the one Command whose success
   * the state it was issued from has **already assumed**.
   *
   * `openExternalAgent` is that Command. The machine reaches `awaitingAuthorizationResponse` on the
   * strength of the request existing, and only then opens the agent — which is what closes the race
   * this shape exists for. Reporting a success would therefore say nothing the state does not already
   * hold, and would say it *late*: on a fast sign-in the callback lands first, so the success event
   * would arrive in `requestingTokens` and file an `auth.illegal_transition` on the happy path.
   *
   * A failure still has to be heard — a spinner sits over the password form until something ends the
   * Grant — so `null` means "nothing to report" and an event means "this is what happened".
   * `commandFailed` is dispatched for an error that escapes, exactly as in {@link on}.
   */
  attempt<D extends Command['do']>(
    verb: D,
    handle: (command: CommandOf<D>) => Promise<AuthEvent | null>
  ): Observable<Action> {
    return this.issued(verb).pipe(
      mergeMap(command =>
        from(handle(command)).pipe(
          mergeMap(event => (event === null ? EMPTY : of(SessionActions.eventReceived({ event })))),
          catchError(() => {
            this.reporter.commandFailed(command);
            return of(SessionActions.eventReceived({ event: { type: 'commandFailed', verb } }));
          })
        )
      )
    );
  }

  /**
   * Run `handle` whenever the machine issues `verb`, and tell the machine nothing either way.
   *
   * For the Commands the machine issues on its way past — `clearVault`, `cognitoSignOut`,
   * `closeExternalAgent`. It has already decided what happens next and is not waiting on any of
   * them, so there is no outcome to report and no wait to end.
   *
   * Separate from {@link on} rather than expressed as a handler returning nothing, because the
   * difference is what a *failure* means, and a thrown error carries no return value to read it
   * from. `closeExternalAgent` is the case that settles it: it is issued in the same breath as
   * `requestTokens`, so a `commandFailed` from an agent that would not close would end a Grant that
   * was going perfectly well.
   *
   * The failure is still reported as `auth.command_failed`: a Vault that would not clear is worth an
   * alert even though nothing is waiting on it.
   */
  teardown<D extends Command['do']>(verb: D, handle: (command: CommandOf<D>) => Promise<void>): Observable<Action> {
    return this.issued(verb).pipe(
      mergeMap(command =>
        from(handle(command)).pipe(
          mergeMap(() => EMPTY),
          catchError(() => {
            this.reporter.commandFailed(command);
            return EMPTY;
          })
        )
      )
    );
  }
}
